import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Trash2, Plus, Lock } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../lib/i18n';

export default function Diary() {
  const { t } = useI18n();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', mood: '', tags: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get('/diary'); setEntries(data); }
    catch { setEntries([]); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    load();
  }, [user, loading, navigate]);

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { title: form.title, body: form.body, mood: form.mood || null, tags: form.tags ? form.tags.split(',').map(x => x.trim()).filter(Boolean) : [] };
      await api.post('/diary', payload);
      toast.success('OK');
      setForm({ title: '', body: '', mood: '', tags: '' });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.common.error);
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (id) => {
    try { await api.delete(`/diary/${id}`); setEntries((e) => e.filter(x => x.id !== id)); }
    catch { toast.error(t.common.error); }
  };

  if (loading || !user) return <div className="max-w-3xl mx-auto px-6 py-24 text-muted-foreground">{t.common.loading}</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">Tikkun</p>
      <h1 className="font-display text-5xl md:text-6xl font-medium tracking-tight leading-tight">{t.diary.title}</h1>
      <p className="mt-4 text-lg text-muted-foreground">{t.diary.subtitle}</p>

      <div className="mt-6 p-4 rounded-2xl bg-secondary border border-border flex items-start gap-3">
        <Lock size={16} className="text-brand-gold-dark mt-0.5" />
        <p className="text-sm text-muted-foreground leading-relaxed">{t.diary.privacy_note}</p>
      </div>

      <div className="mt-8">
        <button onClick={() => setShowForm(!showForm)} data-testid="diary-new-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity">
          <Plus size={16} /> {t.diary.new}
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="mt-6 p-8 rounded-3xl border border-border bg-card space-y-5" data-testid="diary-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.diary.new_title}</label>
            <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="diary-title-input"
              className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.diary.new_body}</label>
            <textarea rows={8} required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="diary-body-input"
              className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary outline-none resize-none" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.diary.mood}</label>
              <input type="text" value={form.mood} onChange={(e) => setForm({ ...form, mood: e.target.value })}
                data-testid="diary-mood-input"
                className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">{t.diary.tags}</label>
              <input type="text" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                data-testid="diary-tags-input"
                className="mt-2 w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary outline-none" />
            </div>
          </div>
          <button type="submit" disabled={submitting} data-testid="diary-save-btn"
            className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-40 transition-opacity">
            {submitting ? t.common.loading : t.diary.save}
          </button>
        </form>
      )}

      <div className="mt-12 space-y-4" data-testid="diary-list">
        {busy ? <p className="text-muted-foreground">{t.common.loading}</p> : entries.length === 0 ? (
          <p className="text-muted-foreground text-center py-16">{t.diary.empty}</p>
        ) : entries.map((e) => (
          <div key={e.id} data-testid={`diary-entry-${e.id}`} className="p-6 rounded-2xl border border-border bg-card">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                  <span>{new Date(e.created_at).toLocaleDateString()}</span>
                  {e.mood && <span>· {e.mood}</span>}
                </div>
                <h3 className="font-display text-lg font-medium">{e.title}</h3>
                <p className="mt-2 text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{e.body}</p>
                {e.tags?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {e.tags.map(tag => <span key={tag} className="text-xs px-2.5 py-0.5 rounded-full bg-secondary">#{tag}</span>)}
                  </div>
                )}
              </div>
              <button onClick={() => del(e.id)} data-testid={`diary-delete-${e.id}`}
                className="text-muted-foreground hover:text-destructive transition-colors">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
